'use client';

import { useTranslate } from '@tolgee/react';
import { useState, useEffect } from 'react';

import { cn } from '@/lib/utils';

interface DevModeIndicatorProps {
  appEnv: string;
  nodeEnv: string;
  isOfflineMode: boolean;
  tolgeeDevToolsEnv: boolean;
}

/**
 * Development mode indicator that shows helpful information for developers
 * Only visible in development mode
 */
export function DevModeIndicator({ appEnv, nodeEnv, isOfflineMode, tolgeeDevToolsEnv }: DevModeIndicatorProps) {
  const { t } = useTranslate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLocalStorageAvailable, setIsLocalStorageAvailable] = useState(false);

  useEffect(() => {
    // Check if localStorage is available
    try {
      const testKey = '__test__';
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      setIsLocalStorageAvailable(true);

      // Load expanded state from localStorage
      const savedState = localStorage.getItem('devtools_expanded');
      if (savedState !== null) {
        setIsExpanded(savedState === 'true');
      }
    } catch (e) {
      setIsLocalStorageAvailable(false);
    }
  }, []);

  // Save expanded state to localStorage
  useEffect(() => {
    if (isLocalStorageAvailable) {
      localStorage.setItem('devtools_expanded', String(isExpanded));
    }
  }, [isExpanded, isLocalStorageAvailable]);

  return (
    <div>
      {isExpanded ? (
        <div className="h-44 w-72 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg bg-gray-800/90 p-3 shadow-lg backdrop-blur-sm">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">{t('component.dev-mode.title', 'Dev Tools')}</h3>
            <button
              className="rounded-full bg-gray-700 p-1 text-gray-300 hover:bg-gray-600 hover:text-white"
              onClick={() => setIsExpanded(false)}>
              <svg xmlns="http://www.w3.org/2000/svg" className="size-3" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>

          <div className="flex flex-col gap-2 text-xs">
            <div className="flex items-center justify-between gap-8">
              <span className="text-gray-300">{t('component.dev-mode.app-env-label', 'APP_ENV')}</span>
              <span
                className={cn(
                  'font-medium',
                  appEnv === 'offline'
                    ? 'text-amber-400'
                    : appEnv === 'local'
                      ? 'text-green-400'
                      : appEnv === 'production'
                        ? 'text-red-400'
                        : 'text-blue-400',
                )}>
                {appEnv}
              </span>
            </div>

            <div className="flex items-center justify-between gap-8">
              <span className="text-gray-300">{t('component.dev-mode.mode-label', 'Mode')}</span>
              <span className={cn('font-medium', isOfflineMode ? 'text-amber-400' : 'text-green-400')}>
                {isOfflineMode ? t('component.dev-mode.offline', 'Offline') : t('component.dev-mode.online', 'Online')}
              </span>
            </div>

            <div className="flex items-center justify-between gap-8">
              <span className="text-gray-300">{t('component.dev-mode.node-env-label', 'Node Env')}</span>
              <span className="font-medium text-blue-400">{nodeEnv}</span>
            </div>

            <div className="flex items-center justify-between gap-8">
              <span className="text-gray-300">{t('component.dev-mode.tolgee-label', 'Tolgee DevTools')}</span>
              <span className="font-medium text-blue-400">{tolgeeDevToolsEnv}</span>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsExpanded(true)}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full shadow-lg hover:bg-gray-700',
            isOfflineMode ? 'bg-amber-600/90 text-white' : 'bg-gray-800/90 text-white',
          )}
          title={t('component.dev-mode.show-button-title', 'Show Dev Tools')}>
          <span className="text-lg font-bold">D</span>
        </button>
      )}
    </div>
  );
}
